import type {
  CanonicalTreeExternalEvidenceObservabilitySummary,
} from './CanonicalTreeExternalEvidenceObservability';

import type {
  CanonicalTreeExternalEvidenceContextAdoptionAction,
} from './CanonicalTreeExternalEvidenceContextAdoptionSimulation';

import type {
  CanonicalTreeExternalEvidenceRefreshReason,
  CanonicalTreeExternalEvidenceSnapshotStatus,
} from './CanonicalTreeExternalEvidenceSnapshot';

export const CANONICAL_TREE_EXTERNAL_EVIDENCE_FIELDS =
  Object.freeze([
    'action',
    'error',
    'lastAttemptStatus',
    'memberCount',
    'ownerUserId',
    'readyForGateway',
    'refreshReason',
    'status',
  ] as const);

export type CanonicalTreeExternalEvidenceField =
  typeof CANONICAL_TREE_EXTERNAL_EVIDENCE_FIELDS[number];

export type CanonicalTreeExternalEvidenceListener = (
  snapshot:
    CanonicalTreeExternalEvidenceObservabilitySummary | null,
) => void;

export type CanonicalTreeExternalEvidenceUnsubscribe =
  () => void;

export type CanonicalTreeExternalEvidenceCandidateListener = (
  candidate: unknown,
) => void;

export interface CanonicalTreeExternalEvidenceReadOnlySource {
  readonly readCurrent: () => unknown;
  readonly subscribe: (
    listener: CanonicalTreeExternalEvidenceCandidateListener,
  ) => CanonicalTreeExternalEvidenceUnsubscribe;
}

export interface CanonicalTreeExternalEvidenceReadOnlyAdapter {
  readonly readSnapshot: () =>
    CanonicalTreeExternalEvidenceObservabilitySummary | null;
  readonly subscribe: (
    listener: CanonicalTreeExternalEvidenceListener,
  ) => CanonicalTreeExternalEvidenceUnsubscribe;
}

const hasExactFields = (
  candidate: Record<string, unknown>,
): boolean => {
  const actual =
    Object.keys(candidate).sort();

  const expected =
    [...CANONICAL_TREE_EXTERNAL_EVIDENCE_FIELDS].sort();

  return (
    actual.length === expected.length &&
    actual.every((
      field,
      index,
    ) => field === expected[index])
  );
};

const VALID_STATUS_VALUES =
  Object.freeze([
    'UNAVAILABLE',
    'LOADING',
    'LOADED',
    'FAILED',
    'STALE',
  ] satisfies readonly CanonicalTreeExternalEvidenceSnapshotStatus[]);

const VALID_REFRESH_REASON_VALUES =
  Object.freeze([
    'INITIAL-GRAPH-LOAD',
    'GRAPH-OWNER-CHANGED',
    'MEMBERS-CHANGED',
    'SPOUSE-WRITE',
    'RELATIONSHIP-HISTORY-WRITE',
    'MANUAL-REFRESH',
  ] satisfies readonly CanonicalTreeExternalEvidenceRefreshReason[]);

const VALID_ACTION_VALUES =
  Object.freeze([
    'INITIALIZE',
    'REPLACE',
    'RETAIN',
    'INVALIDATE',
  ] satisfies readonly CanonicalTreeExternalEvidenceContextAdoptionAction[]);

const isAllowedString = (
  value: unknown,
  allowed: readonly string[],
): boolean =>
  typeof value === 'string' &&
  allowed.includes(value);

export const validateCanonicalTreeExternalEvidenceSummary = (
  candidate: unknown,
): CanonicalTreeExternalEvidenceObservabilitySummary | null => {
  if (
    candidate === null ||
    typeof candidate !== 'object' ||
    Array.isArray(candidate)
  ) {
    return null;
  }

  const record =
    candidate as Record<string, unknown>;

  if (!hasExactFields(record)) {
    return null;
  }

  if (
    typeof record.memberCount !== 'number' ||
    Number.isInteger(record.memberCount) === false ||
    record.memberCount < 0 ||
    typeof record.readyForGateway !== 'boolean' ||
    (
      record.ownerUserId !== null &&
      typeof record.ownerUserId !== 'string'
    ) ||
    (
      record.error !== null &&
      typeof record.error !== 'string'
    ) ||
    (
      record.lastAttemptStatus !== null &&
      !isAllowedString(
        record.lastAttemptStatus,
        VALID_STATUS_VALUES,
      )
    ) ||
    !isAllowedString(
      record.status,
      VALID_STATUS_VALUES,
    ) ||
    (
      record.refreshReason !== null &&
      !isAllowedString(
        record.refreshReason,
        VALID_REFRESH_REASON_VALUES,
      )
    ) ||
    !isAllowedString(
      record.action,
      VALID_ACTION_VALUES,
    )
  ) {
    return null;
  }

  return Object.freeze({
    action: record.action,
    error: record.error,
    lastAttemptStatus: record.lastAttemptStatus,
    memberCount: record.memberCount,
    ownerUserId: record.ownerUserId,
    readyForGateway: record.readyForGateway,
    refreshReason: record.refreshReason,
    status: record.status,
  }) as CanonicalTreeExternalEvidenceObservabilitySummary;
};

export const createCanonicalTreeExternalEvidenceReadOnlyAdapter = (
  source: CanonicalTreeExternalEvidenceReadOnlySource,
): CanonicalTreeExternalEvidenceReadOnlyAdapter =>
  Object.freeze({
    readSnapshot: () => {
      try {
        return validateCanonicalTreeExternalEvidenceSummary(
          source.readCurrent(),
        );
      } catch {
        return null;
      }
    },

    subscribe: (
      listener: CanonicalTreeExternalEvidenceListener,
    ): CanonicalTreeExternalEvidenceUnsubscribe => {
      let active = true;
      let sourceUnsubscribe:
        CanonicalTreeExternalEvidenceUnsubscribe =
          () => {};

      try {
        sourceUnsubscribe =
          source.subscribe((candidate) => {
            if (!active) {
              return;
            }

            const snapshot =
              validateCanonicalTreeExternalEvidenceSummary(
                candidate,
              );

            try {
              listener(snapshot);
            } catch {
              // Consumer diagnostic failure is non-fatal.
            }
          });
      } catch {
        active = false;
      }

      return () => {
        if (!active) {
          return;
        }

        active = false;

        try {
          sourceUnsubscribe();
        } catch {
          // Diagnostic disposal failure is non-fatal.
        }
      };
    },
  });
